import { useState, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "./App.css";
import { loadUserPermissions, hasModuleAccess } from "./utils/permissions";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";

// Import page components - lazy load untuk optimasi
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Kasir from "./pages/Kasir";
import Navigation from "./components/Navigation";
import LandingClaimPhoto from "./pages/LandingClaimPhoto"; // Not lazy - untuk instant load
const Dashboard = lazy(() => import("./pages/Dashboard"));
const History = lazy(() => import("./pages/History"));
const Stok = lazy(() => import("./pages/Stok"));
const ClaimPhoto = lazy(() => import("./pages/ClaimPhoto"));
const Karyawan = lazy(() => import("./pages/Karyawan"));
const PaketPage = lazy(() => import("./pages/PaketPage"));

// Booking pages - lazy load
const BookingPage = lazy(() => import("./pages/BookingPage"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const BookingStatus = lazy(() => import("./pages/BookingStatus"));

// Admin pages - lazy load
const BookingManagement = lazy(() => import("./pages/admin/BookingManagement"));
const Configuration = lazy(() => import("./pages/admin/Configuration"));

function App() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState("kasir");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userPermissions, setUserPermissions] = useState({});
  const [showLogin, setShowLogin] = useState(false);
  const [showClaimPhoto, setShowClaimPhoto] = useState(false);

  // Restore staff session dari localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('snapme_staff_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        console.log('🔄 Restoring staff session:', userData);
        setUser(userData);
      } catch (e) {
        console.error('Failed to parse stored user:', e);
        localStorage.removeItem('snapme_staff_user');
      }
    }
  }, []);

  // Check URL for routes on mount
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/claim-foto' || path === '/claim-photo' || path === '/claim') {
      setShowClaimPhoto(true);
    }
  }, []);

  // Update document title berdasarkan halaman aktif
  useEffect(() => {
    const pageTitle = {
      kasir: "Kasir - SnapMe",
      dashboard: "Dashboard - SnapMe",
      history: "Riwayat Transaksi - SnapMe",
      stok: "Manajemen Produk - SnapMe",
      reservasi: "Reservasi Online - SnapMe",
      claim_photo: "Claim Photo - SnapMe",
      karyawan: "Manajemen Karyawan - SnapMe",
    };

    document.title = user
      ? pageTitle[currentPage] || "Kasir SnapMe"
      : "Login - SnapMe";
  }, [currentPage, user]);

  // Load user permissions when user logs in
  useEffect(() => {
    const loadPermissions = async () => {
      if (user && user.id) {
        try {
          console.log("🔄 Loading permissions for user:", user.id);
          const permissions = await loadUserPermissions(user.id);
          console.log("✅ Permissions loaded:", permissions);
          setUserPermissions(permissions);
        } catch (error) {
          console.error("❌ Failed to load permissions:", error);
          // Set empty permissions as fallback
          setUserPermissions({});
        }
      }
    };

    loadPermissions();
  }, [user]);

  // Handle login
  const handleLogin = async (userData) => {
    setUser(userData);
    // Persist staff session ke localStorage
    localStorage.setItem('snapme_staff_user', JSON.stringify(userData));
    console.log('✅ Staff session saved to localStorage');
  };

  // Handle logout
  const handleLogout = async () => {
    setUser(null);
    setCurrentPage("kasir");
    setUserPermissions({});
    setShowLogin(false); // Kembali ke landing setelah logout
    // Clear staff session dari localStorage
    localStorage.removeItem('snapme_staff_user');
    console.log('🗑️ Staff session cleared from localStorage');
  };

  // Handle login click dari landing page
  const handleLoginClick = () => {
    setShowLogin(true);
  };

  // Handle back to landing dari login page
  const handleBackToLanding = () => {
    setShowLogin(false);
  };

  // Jika belum login (staff)
  if (!user) {
    // Tampilkan claim photo landing jika showClaimPhoto true
    if (showClaimPhoto) {
      return (
        <ThemeProvider>
          <LandingClaimPhoto />
        </ThemeProvider>
      );
    }
    // Tampilkan login jika showLogin true
    if (showLogin) {
      return (
        <Login
          onLogin={handleLogin}
          onBackToLanding={handleBackToLanding}
        />
      );
    }
    // Default tampilkan landing page
    return <Landing onLoginClick={handleLoginClick} />;
  }

  const renderPage = () => {
    // Check if user has access to the current page
    const hasAccess = hasModuleAccess(user, userPermissions, currentPage);

    if (!hasAccess) {
      // Redirect to kasir if no access
      if (currentPage !== "kasir") {
        setCurrentPage("kasir");
      }
      return <Kasir user={user} onLogout={handleLogout} />;
    }

    let pageComponent;
    switch (currentPage) {
      case "kasir":
        pageComponent = <Kasir user={user} onLogout={handleLogout} />;
        break;
      case "dashboard":
        pageComponent = (
          <Suspense
            fallback={
              <div style={{ textAlign: "center", padding: "50px" }}>
                ⏳ Memuat dashboard...
              </div>
            }
          >
            <Dashboard user={user} onLogout={handleLogout} />
          </Suspense>
        );
        break;
      case "history":
        pageComponent = (
          <Suspense
            fallback={
              <div style={{ textAlign: "center", padding: "50px" }}>
                ⏳ Memuat halaman...
              </div>
            }
          >
            <History user={user} onLogout={handleLogout} />
          </Suspense>
        );
        break;
      case "stok":
        pageComponent = (
          <Suspense
            fallback={
              <div style={{ textAlign: "center", padding: "50px" }}>
                ⏳ Memuat halaman...
              </div>
            }
          >
            <Stok user={user} onLogout={handleLogout} />
          </Suspense>
        );
        break;
      case "reservasi":
        pageComponent = (
          <Suspense
            fallback={
              <div style={{ textAlign: "center", padding: "50px" }}>
                ⏳ Memuat halaman...
              </div>
            }
          >
            <BookingManagement user={user} />
          </Suspense>
        );
        break;
      case "claim_photo":
        pageComponent = (
          <Suspense
            fallback={
              <div style={{ textAlign: "center", padding: "50px" }}>
                ⏳ Memuat halaman...
              </div>
            }
          >
            <ClaimPhoto user={user} onLogout={handleLogout} />
          </Suspense>
        );
        break;
      case "karyawan":
        pageComponent = (
          <Suspense
            fallback={
              <div style={{ textAlign: "center", padding: "50px" }}>
                ⏳ Memuat halaman...
              </div>
            }
          >
            <Karyawan user={user} onLogout={handleLogout} />
          </Suspense>
        );
        break;
      case "konfigurasi":
        pageComponent = (
          <Suspense
            fallback={
              <div style={{ textAlign: "center", padding: "50px" }}>
                ⏳ Memuat halaman...
              </div>
            }
          >
            <Configuration />
          </Suspense>
        );
        break;
      default:
        pageComponent = <Kasir user={user} onLogout={handleLogout} />;
    }

    return pageComponent;
  };

  return (
    <AppContent
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      user={user}
      userPermissions={userPermissions}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      onLogout={handleLogout}
      renderPage={renderPage}
    />
  );
}

// Separate component to use theme context
function AppContent({ currentPage, setCurrentPage, user, userPermissions, sidebarOpen, setSidebarOpen, onLogout, renderPage }) {
  const { theme } = useTheme();

  return (
    <div className={`${theme} flex min-h-screen bg-background`}>
      <Navigation
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        user={user}
        userPermissions={userPermissions}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        onLogout={onLogout}
      />
      <div
        className="w-full transition-all duration-300"
        style={{ marginLeft: sidebarOpen ? "256px" : "64px" }}
      >
        {renderPage()}
      </div>
    </div>
  );
}

// Booking Routes Component - handles all /booking/* routes
function BookingRoutes() {
  const [googleUser, setGoogleUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check for existing Google auth session
  useEffect(() => {
    const checkAuth = async () => {
      const { supabaseAuth } = await import('./supabaseClient');
      const { data: { session } } = await supabaseAuth.auth.getSession();

      if (session?.user) {
        setGoogleUser(session.user);
      }
      setLoading(false);

      // Listen for auth changes
      const { data: { subscription } } = supabaseAuth.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setGoogleUser(session.user);
        } else if (event === 'SIGNED_OUT') {
          setGoogleUser(null);
          navigate('/');
        }
      });

      return () => subscription.unsubscribe();
    };

    checkAuth();
  }, [navigate]);

  const handleGoogleLogout = async () => {
    const { supabaseAuth } = await import('./supabaseClient');
    await supabaseAuth.auth.signOut();
    setGoogleUser(null);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Memuat...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!googleUser) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Suspense fallback={<div className="min-h-screen bg-muted flex items-center justify-center">⏳ Memuat...</div>}>
            <BookingPage user={googleUser} onLogout={handleGoogleLogout} />
          </Suspense>
        }
      />
      <Route
        path="/confirmation/:id"
        element={
          <Suspense fallback={<div className="min-h-screen bg-muted flex items-center justify-center">⏳ Memuat...</div>}>
            <BookingConfirmation />
          </Suspense>
        }
      />
      <Route
        path="/status"
        element={
          <Suspense fallback={<div className="min-h-screen bg-muted flex items-center justify-center">⏳ Memuat...</div>}>
            <BookingStatus />
          </Suspense>
        }
      />
    </Routes>
  );
}

// Admin Booking Route Component - requires staff login
function AdminBookingRoute() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in via localStorage (staff login)
    const storedUser = localStorage.getItem('snapme_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Memuat...</p>
        </div>
      </div>
    );
  }

  // If not authenticated as staff, redirect to main app (which will show login)
  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <BookingManagement
      user={user}
      onBack={() => navigate('/')}
    />
  );
}

function AppWrapper() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <Routes>
          {/* Paket page - public */}
          <Route
            path="/paket"
            element={
              <Suspense fallback={<div className="min-h-screen bg-muted flex items-center justify-center">⏳ Memuat...</div>}>
                <PaketPage />
              </Suspense>
            }
          />
          {/* Booking routes - accessible without staff login */}
          <Route path="/booking/*" element={<BookingRoutes />} />
          <Route
            path="/booking/status"
            element={
              <Suspense fallback={<div className="min-h-screen bg-muted flex items-center justify-center">⏳ Memuat...</div>}>
                <BookingStatus />
              </Suspense>
            }
          />
          {/* Admin booking management route */}
          <Route
            path="/admin/bookings"
            element={
              <Suspense fallback={<div className="min-h-screen bg-muted flex items-center justify-center">⏳ Memuat...</div>}>
                <AdminBookingRoute />
              </Suspense>
            }
          />
          {/* Main app routes */}
          <Route path="/*" element={<App />} />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default AppWrapper;
